<template>
  <div ref="container" class="threat-visualizer-d3" :class="{ 'dark-theme': isDarkMode }">
    <svg ref="svg" class="threat-svg">
      <defs>
        <!-- Glow filters for dark theme -->
        <filter id="glow-org" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-cve" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-cwe" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-capec" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-ttp" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-apt" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <!-- Severity glow filters for CVE nodes in dark mode -->
        <filter id="glow-severity-critical" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="4" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-severity-high" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-severity-medium" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <filter id="glow-severity-low" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
          <feMerge><feMergeNode in="coloredBlur" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        <!-- Shadow for light theme -->
        <filter id="node-shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="2" flood-opacity="0.15" />
        </filter>
      </defs>
      <g class="zoom-group">
        <g class="links-group" />
        <g class="nodes-group" />
        <g class="labels-group" />
      </g>
    </svg>

    <!-- Legend -->
    <d3-graph-legend :is-dark="isDarkMode" :node-counts="nodeCounts" :severity-counts="severityCounts" />
  </div>
</template>

<script>
import * as d3 from 'd3';
import D3GraphLegend from './D3GraphLegend.vue';
import { extractVisualizationData } from './threatVisualizerEngine';
import {
  D3_NODE_TYPES,
  getThemeFromCSS,
  NODE_ICONS,
  DEFAULT_RING_DISTANCES,
  buildGraph,
  createZoomBehavior,
  createDragBehavior,
  generateLinkPath,
  getNodeColor,
  getConnectedNodeIds,
  isLinkConnected,
  getSeverityColor,
  getSeverityLevel,
  countNodeConnections,
  getDynamicNodeRadius,
} from './d3GraphUtils';
import { applyPathModeFilter } from '../shared';

export default {
  name: 'ThreatVisualizerD3',
  components: {
    D3GraphLegend,
  },
  props: {
    insightsData: { type: Object, default: null },
    nodeSizes: { type: Object, default: () => ({}) },
    ringRadii: { type: Object, default: () => ({}) },
    selectedCves: { type: Array, default: () => [] },
    isDark: { type: Boolean, default: false },
    assetName: { type: String, default: null },
    assetCorrelation: { type: Object, default: () => ({}) },
    filteredVisualization: { type: Object, default: null },
    pathMode: { type: String, default: 'full' },
    isLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      svg: null,
      zoomBehavior: null,
      nodes: [],
      links: [],
      selectedNode: null,
      justClickedNode: false,
      isAnimating: false,
      hasRenderedOnce: false,
    };
  },
  computed: {
    isDarkMode() {
      return this.isDark;
    },
    theme() {
      // Get theme based on isDark prop
      return getThemeFromCSS(this.isDark);
    },
    nodeCounts() {
      const counts = {};
      this.nodes.forEach(node => {
        if (!counts[node.type]) counts[node.type] = 0;
        counts[node.type] += 1;
      });
      return counts;
    },
    severityCounts() {
      const counts = {
        critical: 0, high: 0, medium: 0, low: 0, none: 0,
      };
      this.nodes.filter(n => n.type === 'cve').forEach(node => {
        const cvss = node.data?.cvss || node.data?.cvss_score || node.cvss;
        const baseSeverity = node.data?.baseSeverity || node.data?.base_severity;
        const level = getSeverityLevel(cvss, baseSeverity);
        counts[level] += 1;
      });
      return counts;
    },
    connectionCounts() {
      return countNodeConnections(this.links);
    },
    maxConnections() {
      if (!this.connectionCounts || this.connectionCounts.size === 0) return 1;
      return Math.max(...this.connectionCounts.values());
    },
  },
  watch: {
    insightsData: {
      handler() {
        // Reset so the new domain gets its entrance animation
        this.hasRenderedOnce = false;
        this.updateVisualization();
      },
      deep: true,
    },

    filteredVisualization: {
      handler() { this.updateVisualization(); },
      deep: true,
    },
    selectedCves: {
      handler() { this.updateVisualization(); },
      deep: true,
    },
    assetCorrelation: {
      handler() { this.updateVisualization(); },
      deep: true,
    },
    pathMode() {
      this.updateVisualization();
    },
    isDark() {
      this.renderGraph();
    },
  },
  mounted() {
    this.initVisualization();
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
  },
  methods: {
    initVisualization() {
      const { container } = this.$refs;
      const svgEl = this.$refs.svg;
      if (!container || !svgEl) return;

      const { width, height } = this.getDimensions();

      this.svg = d3.select(svgEl)
        .attr('width', width)
        .attr('height', height);

      this.zoomBehavior = createZoomBehavior(event => {
        this.svg.select('.zoom-group').attr('transform', event.transform);
      });

      this.svg.call(this.zoomBehavior);
      this.centerView();
      this.updateVisualization();
    },

    getDimensions() {
      const { container } = this.$refs;
      return {
        width: container?.clientWidth || 800,
        height: container?.clientHeight || 400,
      };
    },

    centerView() {
      const { width, height } = this.getDimensions();
      const transform = d3.zoomIdentity.translate(width / 2, height / 2);
      this.svg.call(this.zoomBehavior.transform, transform);
    },

    fitToView() {
      if (!this.nodes || this.nodes.length === 0) {
        this.centerView();
        return;
      }

      const { width, height } = this.getDimensions();
      const padding = 60; // Padding around the graph

      // Calculate bounding box of all nodes
      let minX = Infinity;
      let maxX = -Infinity;
      let minY = Infinity;
      let maxY = -Infinity;

      this.nodes.forEach(node => {
        const nodeRadius = this.getNodeRadiusDynamic(node);
        minX = Math.min(minX, node.x - nodeRadius);
        maxX = Math.max(maxX, node.x + nodeRadius);
        minY = Math.min(minY, node.y - nodeRadius);
        maxY = Math.max(maxY, node.y + nodeRadius);
      });

      const graphWidth = maxX - minX;
      const graphHeight = maxY - minY;

      // Calculate scale to fit the graph in the container
      const scaleX = (width - padding * 2) / graphWidth;
      const scaleY = (height - padding * 2) / graphHeight;
      let scale = Math.min(scaleX, scaleY, 1.2); // Cap at 1.2 to avoid too much zoom

      // Ensure minimum scale
      scale = Math.max(scale, 0.4);

      // Calculate center of the graph
      const graphCenterX = (minX + maxX) / 2;
      const graphCenterY = (minY + maxY) / 2;

      // Calculate translation to center the graph
      const translateX = width / 2 - graphCenterX * scale;
      const translateY = height / 2 - graphCenterY * scale;

      const transform = d3.zoomIdentity
        .translate(translateX, translateY)
        .scale(scale);

      this.svg
        .transition()
        .duration(500)
        .call(this.zoomBehavior.transform, transform);
    },

    updateVisualization() {
      if (!this.insightsData || !this.svg) return;

      // Use pre-filtered visualization data from parent if available
      if (this.filteredVisualization && this.filteredVisualization.nodes) {
        if (this.filteredVisualization.nodes.length === 0) {
          this.nodes = [];
          this.links = [];
          this.renderGraph();
          return;
        }

        const graph = this.convertVisualizationToGraph(
          this.filteredVisualization.nodes,
          this.filteredVisualization.links,
        );
        this.nodes = graph.nodes;
        this.links = graph.links;
        this.renderGraph();
        return;
      }

      // Fallback: Check if we have raw visualization data from backend (no parent filtering)
      const visualization = this.assetCorrelation?.visualization;
      if (visualization && visualization.nodes && visualization.nodes.length > 0) {
        // Use all data when no filtering is applied
        const graph = this.convertVisualizationToGraph(visualization.nodes, visualization.links);
        this.nodes = graph.nodes;
        this.links = graph.links;
        this.renderGraph();
        return;
      }

      // Fallback: Use frontend data extraction (legacy behavior)
      const data = extractVisualizationData(
        this.insightsData,
        this.selectedCves.length > 0 ? this.selectedCves : null,
        this.assetName,
        this.assetCorrelation,
      );
      const graph = buildGraph(data, this.ringRadii);

      this.nodes = graph.nodes;
      this.links = graph.links;

      this.renderGraph();
    },

    /**
     * Convert backend visualization format to D3 graph format
     */
    convertVisualizationToGraph(vizNodes, vizLinks) {
      // First apply path mode filtering
      const { nodes: filteredVizNodes, links: filteredVizLinks } = applyPathModeFilter(
        vizNodes,
        vizLinks,
        this.pathMode,
      );

      const nodes = [];
      const nodeMap = new Map();

      // Convert nodes
      filteredVizNodes.forEach((vizNode, index) => {
        const total = filteredVizNodes.filter(n => n.type === vizNode.type).length;
        const typeIndex = filteredVizNodes.filter((n, i) => n.type === vizNode.type && i < index).length;
        const radius = this.ringRadii[vizNode.type] || DEFAULT_RING_DISTANCES[vizNode.type] || 200;
        const angle = (2 * Math.PI * typeIndex) / Math.max(1, total) - Math.PI / 2;

        // Strip prefix from name for display
        let displayName = vizNode.name;
        const idLower = vizNode.id.toLowerCase();
        if (idLower.startsWith('org:')) displayName = vizNode.id.substring(4);
        else if (idLower.startsWith('asset:')) displayName = vizNode.id.substring(6);
        else if (idLower.startsWith('tech:')) displayName = vizNode.id.substring(5);
        else if (idLower.startsWith('apt:')) displayName = vizNode.id.substring(4);

        const node = {
          id: vizNode.id,
          type: vizNode.type,
          label: displayName,
          x: vizNode.type === 'org' ? 0 : Math.cos(angle) * radius,
          y: vizNode.type === 'org' ? 0 : Math.sin(angle) * radius,
          fx: vizNode.type === 'org' ? 0 : undefined,
          fy: vizNode.type === 'org' ? 0 : undefined,
          data: vizNode.data,
        };

        nodes.push(node);
        nodeMap.set(vizNode.id, node);
      });

      // Convert links with direct node references
      const links = filteredVizLinks.map(vizLink => ({
        source: nodeMap.get(vizLink.source),
        target: nodeMap.get(vizLink.target),
        type: vizLink.type,
      })).filter(l => l.source && l.target);

      return { nodes, links };
    },

    /**
     * Filter nodes and links based on path mode
     * - full: All columns (Asset → Tech → CVE → CWE → CAPEC → TTP → APT)
     * - attack: CVE → CWE → CAPEC → TTP → APT (hide Asset/Tech)
     * - threat: CVE → TTP → APT (hide CWE/CAPEC)
     * - direct: CVE → APT (minimal view)
     */

    renderGraph() {
      if (!this.svg || this.nodes.length === 0) {
        return;
      }

      const zoomGroup = this.svg.select('.zoom-group');
      // Get theme colors based on current dark mode state
      const theme = getThemeFromCSS(this.isDark);

      // Interrupt all ongoing transitions and clear all groups
      zoomGroup.selectAll('*').interrupt();
      zoomGroup.select('.links-group').selectAll('*').remove();
      zoomGroup.select('.nodes-group').selectAll('*').remove();
      zoomGroup.select('.labels-group').selectAll('*').remove();

      this.renderLinks(zoomGroup, theme);
      this.renderNodes(zoomGroup, theme);
      this.renderLabels(zoomGroup, theme);

      // Only animate once per mount — skip re-renders from data updates, theme changes, etc.
      if (!this.hasRenderedOnce) {
        this.hasRenderedOnce = true;
        this.animateEntrance(zoomGroup, theme);
      }

      // Fit the graph to view after rendering
      this.$nextTick(() => {
        this.fitToView();
      });
    },

    renderLinks(zoomGroup, theme) {
      const linksGroup = zoomGroup.select('.links-group');
      linksGroup.selectAll('*').remove();

      linksGroup.selectAll('path')
        .data(this.links)
        .join('path')
        .attr('class', 'link')
        .attr('d', generateLinkPath)
        .attr('stroke', d => {
          const sourceNode = this.nodes.find(n => n.id === (d.source.id || d.source));
          return getNodeColor(sourceNode || {}, theme);
        })
        .attr('stroke-opacity', theme.linkOpacity)
        .attr('stroke-width', 1.5)
        .attr('fill', 'none');
    },

    renderNodes(zoomGroup, theme) {
      const self = this;
      const nodesGroup = zoomGroup.select('.nodes-group');

      nodesGroup.selectAll('*').remove();

      // Helper to safely get coordinates
      const safeCoord = val => ((typeof val === 'number' && !Number.isNaN(val)) ? val : 0);

      // Drag callback to update positions
      const onDrag = () => {
        zoomGroup.selectAll('.node')
          .attr('transform', d => `translate(${safeCoord(d.x)},${safeCoord(d.y)})`);
        zoomGroup.selectAll('.link')
          .attr('d', generateLinkPath);
        zoomGroup.selectAll('.label-group')
          .attr('transform', d => `translate(${safeCoord(d.x)}, ${safeCoord(d.y) + self.getNodeRadiusDynamic(d) + 14})`);
      };

      // Click handler passed to drag behavior - called when no drag occurred
      const handleNodeClick = (event, d) => {
        // Set flag to prevent background click from firing
        self.justClickedNode = true;
        setTimeout(() => { self.justClickedNode = false; }, 50);
        if (event.sourceEvent) {
          event.sourceEvent.stopPropagation();
        }
        self.selectNode(d);
      };

      const dragBehavior = createDragBehavior(onDrag, handleNodeClick);

      // Filter out nodes with invalid positions
      const validNodes = this.nodes.filter(d => d && typeof d.x === 'number' && typeof d.y === 'number' && !Number.isNaN(d.x) && !Number.isNaN(d.y));

      const nodeGroups = nodesGroup.selectAll('g')
        .data(validNodes)
        .join('g')
        .attr('class', 'node')
        .attr('transform', d => `translate(${safeCoord(d.x)},${safeCoord(d.y)})`)
        .style('cursor', d => (d.type === D3_NODE_TYPES.ORG ? 'default' : 'grab'))
        .call(dragBehavior);

      // Severity ring for CVE nodes (rendered first, behind main circle)
      nodeGroups.filter(d => d.type === D3_NODE_TYPES.CVE)
        .append('circle')
        .attr('class', 'severity-ring')
        .attr('r', d => self.getNodeRadiusDynamic(d) + 4)
        .attr('fill', 'none')
        .attr('stroke', d => {
          // Try multiple paths to find CVSS score or baseSeverity
          const cvss = d.data?.cvss || d.data?.cvss_score || d.cvss || d.cvss_score;
          const baseSeverity = d.data?.baseSeverity || d.data?.base_severity || d.baseSeverity;
          return getSeverityColor(cvss, this.isDark, baseSeverity);
        })
        .attr('stroke-width', 3)
        .attr('filter', d => {
          if (!this.isDark) return null;
          const cvss = d.data?.cvss || d.data?.cvss_score || d.cvss || d.cvss_score;
          const baseSeverity = d.data?.baseSeverity || d.data?.base_severity || d.baseSeverity;
          const level = getSeverityLevel(cvss, baseSeverity);
          return level !== 'none' ? `url(#glow-severity-${level})` : null;
        })
        .attr('opacity', d => {
          // Hide ring if no severity data
          const cvss = d.data?.cvss || d.data?.cvss_score || d.cvss || d.cvss_score;
          const baseSeverity = d.data?.baseSeverity || d.data?.base_severity || d.baseSeverity;
          return getSeverityLevel(cvss, baseSeverity) !== 'none' ? 1 : 0;
        });

      // Background circle - using dynamic sizing
      nodeGroups.append('circle')
        .attr('r', d => self.getNodeRadiusDynamic(d))
        .attr('fill', d => getNodeColor(d, theme))
        .attr('stroke', this.isDark ? 'none' : 'rgba(0,0,0,0.1)')
        .attr('stroke-width', this.isDark ? 0 : 1)
        .attr('filter', d => (this.isDark ? `url(#glow-${d.type})` : null))
        .attr('class', 'node-circle');

      // Icon - MDI icons are 24x24, center at (12,12)
      const iconScale = 0.55;
      nodeGroups.append('path')
        .attr('d', d => NODE_ICONS[d.type])
        .attr('fill', this.isDark ? 'rgba(0,0,0,0.6)' : '#fff')
        .attr('transform', d => {
          const r = self.getNodeRadiusDynamic(d);
          const scale = (r * iconScale) / 12;
          // Center the 24x24 icon: translate by -12*scale to center
          return `translate(${-12 * scale},${-12 * scale}) scale(${scale})`;
        })
        .attr('class', 'node-icon');

      // Event handlers for hover only
      nodeGroups
        .on('mouseenter', function onEnter(_, d) {
          if (self.isAnimating) return;
          self.handleNodeHover(d, true);
          d3.select(this).select('.node-circle')
            .interrupt('hover')
            .transition('hover')
            .duration(150)
            .attr('r', self.getNodeRadiusDynamic(d) * 1.15);
        })
        .on('mouseleave', function onLeave(_, d) {
          if (self.isAnimating) return;
          if (self.selectedNode?.id !== d.id) {
            self.handleNodeHover(d, false);
          }
          d3.select(this).select('.node-circle')
            .interrupt('hover')
            .transition('hover')
            .duration(150)
            .attr('r', self.getNodeRadiusDynamic(d));
        });

      this.svg.on('click', () => {
        // Don't clear selection if we just clicked a node
        if (self.justClickedNode) return;
        self.selectNode(null);
      });
    },

    renderLabels(zoomGroup, theme) {
      const self = this;
      const labelsGroup = zoomGroup.select('.labels-group');
      labelsGroup.selectAll('*').remove();

      // Filter out nodes with invalid positions
      const validNodes = this.nodes.filter(d => d && typeof d.x === 'number' && typeof d.y === 'number' && !Number.isNaN(d.x) && !Number.isNaN(d.y));

      // Create label groups for each node (background rect + text)
      const labelGroups = labelsGroup.selectAll('g.label-group')
        .data(validNodes)
        .join('g')
        .attr('class', 'label-group')
        .attr('transform', d => {
          const x = d.x || 0;
          const y = (d.y || 0) + self.getNodeRadiusDynamic(d) + 14;
          return `translate(${x}, ${y})`;
        });

      // Add background rect first (will be sized after text is rendered)
      labelGroups.append('rect')
        .attr('class', 'label-bg')
        .attr('fill', this.isDarkMode ? 'rgba(20, 20, 30, 0.85)' : 'rgba(255, 255, 255, 0.9)')
        .attr('rx', 3)
        .attr('ry', 3);

      // Add text
      labelGroups.append('text')
        .attr('class', 'node-label')
        .attr('text-anchor', 'middle')
        .attr('x', 0)
        .attr('y', 0)
        .attr('fill', theme.text)
        .attr('font-size', d => (d.type === D3_NODE_TYPES.ORG ? 12 : 10))
        .attr('font-weight', d => (d.type === D3_NODE_TYPES.ORG ? 600 : 400))
        .attr('opacity', 1)
        .text(d => d.label || ''); // Show full label, no truncation

      // Size the background rects based on text dimensions
      labelGroups.each(function adjustBg() {
        const group = d3.select(this);
        const text = group.select('text');
        const textNode = text.node();
        if (textNode) {
          const bbox = textNode.getBBox();
          group.select('.label-bg')
            .attr('x', bbox.x - 4)
            .attr('y', bbox.y - 2)
            .attr('width', bbox.width + 8)
            .attr('height', bbox.height + 4);
        }
      });
    },

    animateEntrance(zoomGroup, theme) {
      // Set animation guard to prevent hover from interrupting
      this.isAnimating = true;

      // Animate nodes appearing in a spiral pattern
      const nodesByType = {};
      this.nodes.forEach(node => {
        if (!nodesByType[node.type]) nodesByType[node.type] = [];
        nodesByType[node.type].push(node);
      });

      // Define the order of appearance (from center outward)
      const typeOrder = ['org', 'asset', 'tech', 'cve', 'cwe', 'capec', 'ttp', 'apt'];

      let globalDelay = 0;
      const delayIncrement = 40; // ms between each node
      const animationDuration = 500;
      let maxDelay = 0;

      typeOrder.forEach(type => {
        const nodesOfType = nodesByType[type] || [];
        nodesOfType.forEach((node, index) => {
          const delay = globalDelay + (index * delayIncrement);
          if (delay + animationDuration > maxDelay) maxDelay = delay + animationDuration;

          // Start nodes at scale 0 and fade in — use named transition
          const nodeSelection = zoomGroup.selectAll('.node')
            .filter(d => d.id === node.id);

          nodeSelection
            .attr('transform', d => {
              const x = (d && typeof d.x === 'number' && !Number.isNaN(d.x)) ? d.x : 0;
              const y = (d && typeof d.y === 'number' && !Number.isNaN(d.y)) ? d.y : 0;
              return `translate(${x},${y}) scale(0)`;
            })
            .attr('opacity', 0)
            .transition('entrance')
            .delay(delay)
            .duration(animationDuration)
            .ease(d3.easeBackOut.overshoot(1.5))
            .attr('transform', d => {
              const x = (d && typeof d.x === 'number' && !Number.isNaN(d.x)) ? d.x : 0;
              const y = (d && typeof d.y === 'number' && !Number.isNaN(d.y)) ? d.y : 0;
              return `translate(${x},${y}) scale(1)`;
            })
            .attr('opacity', 1);
        });

        globalDelay += nodesOfType.length * delayIncrement;
      });

      // Animate links fading in after nodes start appearing — named transition
      zoomGroup.selectAll('.link')
        .attr('stroke-opacity', 0)
        .transition('entrance')
        .delay(250)
        .duration(700)
        .attr('stroke-opacity', theme.linkOpacity);

      // Animate labels fading in last — named transition
      zoomGroup.selectAll('.label-group')
        .attr('opacity', 0)
        .transition('entrance')
        .delay(400)
        .duration(600)
        .attr('opacity', 1);

      // Clear animation guard after all transitions complete
      const totalDuration = Math.max(maxDelay, 400 + 600);
      setTimeout(() => { this.isAnimating = false; }, totalDuration);
    },

    handleNodeHover(node, isHovering) {
      if (!node) return;
      const zoomGroup = this.svg.select('.zoom-group');
      const { theme } = this;

      // If not hovering, check if there's a selected node to highlight instead
      if (!isHovering) {
        if (this.selectedNode) {
          // Re-apply the selected node highlighting
          const selectedConnectedIds = getConnectedNodeIds(this.selectedNode.id, this.links);

          zoomGroup.selectAll('.node')
            .transition('hover').duration(150)
            .attr('opacity', d => (selectedConnectedIds.has(d.id) ? 1 : 0.2));

          zoomGroup.selectAll('.link')
            .transition('hover').duration(150)
            .attr('stroke-opacity', d => (isLinkConnected(d, this.selectedNode.id, selectedConnectedIds) ? 0.7 : 0.05))
            .attr('stroke-width', d => (isLinkConnected(d, this.selectedNode.id, selectedConnectedIds) ? 2.5 : 1));

          zoomGroup.selectAll('.node-label')
            .transition('hover').duration(150)
            .attr('opacity', d => (selectedConnectedIds.has(d.id) ? 1 : 0.1));
        } else {
          // No selection, reset to neutral
          zoomGroup.selectAll('.node')
            .transition('hover').duration(150)
            .attr('opacity', 1);

          zoomGroup.selectAll('.link')
            .transition('hover').duration(150)
            .attr('stroke-opacity', theme.linkOpacity)
            .attr('stroke-width', 1.5);

          zoomGroup.selectAll('.node-label')
            .transition('hover').duration(150)
            .attr('opacity', 0.9);
        }
        return;
      }

      // Hovering - highlight the hovered node's connections
      const connectedIds = getConnectedNodeIds(node.id, this.links);

      zoomGroup.selectAll('.node')
        .transition('hover').duration(150)
        .attr('opacity', d => (connectedIds.has(d.id) ? 1 : 0.2));

      zoomGroup.selectAll('.link')
        .transition('hover').duration(150)
        .attr('stroke-opacity', d => (isLinkConnected(d, node.id, connectedIds) ? 0.7 : 0.05))
        .attr('stroke-width', d => (isLinkConnected(d, node.id, connectedIds) ? 2.5 : 1));

      zoomGroup.selectAll('.node-label')
        .transition('hover').duration(150)
        .attr('opacity', d => (connectedIds.has(d.id) ? 1 : 0.1));
    },

    selectNode(node) {
      // Toggle behavior: if clicking the same node, deselect it
      if (this.selectedNode && this.selectedNode.id === node?.id) {
        this.selectedNode = null;
        this.resetToNeutral();
        this.$emit('node-select', null);
        return;
      }
      // Clear previous selection
      this.selectedNode = node;
      if (node) {
        this.handleNodeHover(node, true);
      } else {
        // Clicking background - reset to neutral
        this.resetToNeutral();
      }
      this.$emit('node-select', node);
    },

    resetToNeutral() {
      const zoomGroup = this.svg.select('.zoom-group');
      const { theme } = this;

      zoomGroup.selectAll('.node')
        .transition('hover').duration(150)
        .attr('opacity', 1);

      zoomGroup.selectAll('.link')
        .transition('hover').duration(150)
        .attr('stroke-opacity', theme.linkOpacity)
        .attr('stroke-width', 1.5);

      zoomGroup.selectAll('.node-label')
        .transition('hover').duration(150)
        .attr('opacity', 0.9);
    },

    clearSelection() {
      this.selectNode(null);
    },

    handleResize() {
      if (!this.$refs.container) return;
      const { width, height } = this.getDimensions();
      this.svg.attr('width', width).attr('height', height);
    },

    highlightAndCenterNode(nodeId) {
      const node = this.nodes.find(n => n.id === nodeId);
      if (!node) return;

      // Select the node (this will highlight its connections)
      this.selectNode(node);

      // Center the view on this node
      const { width, height } = this.getDimensions();
      const scale = 1.2; // Zoom in a bit

      const transform = d3.zoomIdentity
        .translate(width / 2 - node.x * scale, height / 2 - node.y * scale)
        .scale(scale);

      this.svg
        .transition()
        .duration(500)
        .call(this.zoomBehavior.transform, transform);

      // Add a pulsing effect to the highlighted node
      const zoomGroup = this.svg.select('.zoom-group');
      const nodeGroup = zoomGroup.selectAll('.node')
        .filter(d => d.id === nodeId);

      const nodeRadius = this.getNodeRadiusDynamic(node);
      nodeGroup.select('.node-circle')
        .transition()
        .duration(200)
        .attr('r', nodeRadius * 1.3)
        .transition()
        .duration(200)
        .attr('r', nodeRadius * 1.15)
        .transition()
        .duration(200)
        .attr('r', nodeRadius * 1.25)
        .transition()
        .duration(200)
        .attr('r', nodeRadius * 1.15);
    },

    clearHighlight() {
      this.selectNode(null);
      this.fitToView();
    },

    setNodeSizes(sizes) {
      Object.assign(this.nodeSizes, sizes);
      this.renderGraph();
    },

    setRingRadii(radii) {
      Object.assign(this.ringRadii, radii);
      this.updateVisualization();
    },

    /**
     * Get dynamic node radius based on connection count
     */
    getNodeRadiusDynamic(node) {
      return getDynamicNodeRadius(
        node,
        this.nodeSizes,
        this.connectionCounts,
        this.maxConnections,
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.threat-visualizer-d3 {
  width: 100%;
  height: 100%;
  position: relative;
  background: var(--graph-bg);
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--graph-border);
  transition: background 0.3s ease, border-color 0.3s ease;

  &.dark-theme {
    background: radial-gradient(ellipse at center, #1a1a2e 0%, #0a0a1a 100%);
  }
}

.threat-svg {
  width: 100%;
  height: 100%;
  cursor: grab;

  &:active {
    cursor: grabbing;
  }
}
</style>

<style lang="scss">
#app .threat-visualizer-d3 {
  .link {
    pointer-events: none;
  }

  .node {
    transition: opacity 0.15s ease;
  }

  .node-circle {
    transition: r 0.15s ease;
  }

  .node-icon {
    pointer-events: none;
  }

  .node-label {
    pointer-events: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  }
}
</style>
